import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { normalizeDate, formatDateString } from '../utils/dateUtils';

/**
 * CalendarView - Displays weekly or monthly calendar
 * Props:
 * - viewMode: 'weekly' | 'monthly' (controlled by parent)
 * - onDateSelect: callback when a day is clicked
 * - selectedDate: currently selected date string (YYYY-MM-DD)
 * - renderDayContent: optional function to render custom content in each day cell
 * - showToggle: whether to show internal Weekly/Monthly toggle (default: false for parent control)
 * - monthOffset: optional controlled month offset for monthly view
 * - onMonthChange: callback when month changes (for controlled mode)
 * - weekOffset: optional controlled week offset for weekly view
 * - onWeekChange: callback when week changes (for controlled mode)
 * - weekDates: EXPLICIT week dates array (SINGLE SOURCE OF TRUTH - if provided, overrides internal calculation)
 * - hideHeader: hide the internal navigation header (when parent handles it)
 */
function CalendarView({ viewMode = 'weekly', onDateSelect, selectedDate, renderDayContent, showToggle = false, onViewModeChange, monthOffset, onMonthChange, weekOffset, onWeekChange, weekDates, hideHeader = false }) {
  const [internalDate, setInternalDate] = useState(new Date());
  
  // Use controlled month offset if provided, otherwise use internal state
  const currentDate = useMemo(() => {
    if (viewMode === 'monthly' && monthOffset !== undefined) {
      const now = normalizeDate(new Date());
      return new Date(now.getFullYear(), now.getMonth() + monthOffset, 1, 12, 0, 0);
    }
    if (viewMode === 'weekly' && weekOffset !== undefined) {
      const now = normalizeDate(new Date());
      const dayOfWeek = now.getDay(); // 0 = Sunday
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - dayOfWeek + (weekOffset * 7));
      return startOfWeek;
    }
    return normalizeDate(internalDate);
  }, [viewMode, monthOffset, weekOffset, internalDate]);
  
  const today = normalizeDate(new Date());

  // Get week start (Sunday) - uses normalized dates for timezone safety
  const getWeekStart = (date) => {
    const d = normalizeDate(date);
    const day = d.getDay(); // 0 = Sunday
    d.setDate(d.getDate() - day);
    return d;
  };

  // Get month data
  const getMonthData = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Get the day of week for first day (0 = Sunday)
    const startDay = firstDay.getDay();
    
    return { year, month, daysInMonth, startDay, firstDay, lastDay };
  };

  // Get week days - USE EXPLICIT weekDates if provided (SINGLE SOURCE OF TRUTH)
  const weekDays = useMemo(() => {
    // If explicit weekDates provided by parent, use those (ensures alignment)
    if (weekDates && weekDates.length === 7) {
      console.log('[CalendarView] Using EXPLICIT weekDates from parent:', weekDates);
      return weekDates.map(dateStr => new Date(dateStr + 'T00:00:00'));
    }
    
    // Otherwise calculate internally (for backwards compatibility)
    const weekStart = getWeekStart(currentDate);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    console.log('[CalendarView] Calculated weekDays internally:', days.map(d => formatDateString(d)));
    return days;
  }, [currentDate, weekDates]);

  // Get month grid
  const monthGrid = useMemo(() => {
    const { daysInMonth, startDay } = getMonthData(currentDate);
    const grid = [];
    
    // Empty cells before first day
    for (let i = 0; i < startDay; i++) {
      grid.push(null);
    }
    
    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      grid.push(d);
    }
    
    return grid;
  }, [currentDate]);

  // Navigation
  const navigate = (direction) => {
    if (viewMode === 'monthly' && monthOffset !== undefined && onMonthChange) {
      // Controlled mode for monthly - update parent state
      onMonthChange(monthOffset + direction);
    } else if (viewMode === 'weekly' && weekOffset !== undefined && onWeekChange) {
      // Controlled mode for weekly - update parent state
      onWeekChange(weekOffset + direction);
    } else {
      // Internal state mode
      const newDate = new Date(currentDate);
      if (viewMode === 'weekly') {
        newDate.setDate(newDate.getDate() + (direction * 7));
      } else {
        newDate.setMonth(newDate.getMonth() + direction);
      }
      setInternalDate(newDate);
    }
  };

  // Check if date is today
  const isToday = (date) => {
    if (!date) return false;
    return date.toDateString() === today.toDateString();
  };

  // Check if date is selected
  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    return formatDateString(date) === selectedDate;
  };

  // Header text
  const headerText = useMemo(() => {
    if (viewMode === 'weekly') {
      const start = weekDays[0];
      const end = weekDays[6];
      const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
      const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
      
      if (startMonth === endMonth) {
        return `${startMonth} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
      }
      return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
    }
    return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [viewMode, currentDate, weekDays]);

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border/40 rounded-xl overflow-hidden"
      data-testid="calendar-view"
    >
      {/* Header - can be hidden when parent handles navigation */}
      {!hideHeader && (
        <div className="p-3 border-b border-border/40">
          {/* View Mode Toggle - only if showToggle */}
          {showToggle && onViewModeChange && (
            <div className="flex justify-center mb-3">
              <div className="inline-flex p-1 bg-secondary/50 rounded-lg">
                <button
                  onClick={() => onViewModeChange('weekly')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                    viewMode === 'weekly'
                      ? 'bg-accent text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="toggle-weekly"
                >
                  Weekly
                </button>
                <button
                  onClick={() => onViewModeChange('monthly')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                    viewMode === 'monthly'
                      ? 'bg-accent text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="toggle-monthly"
                >
                  Monthly
                </button>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-8 w-8 rounded-full"
              data-testid="calendar-prev"
            >
              <ChevronLeft size={18} />
            </Button>
            <span className="text-sm font-medium">{headerText}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(1)}
              className="h-8 w-8 rounded-full"
              data-testid="calendar-next"
            >
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Day Headers */}
      <div className="grid grid-cols-7 border-b border-border/40">
        {dayNames.map((day) => (
          <div key={day} className="py-2 text-center text-xs font-medium text-muted-foreground">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      {viewMode === 'weekly' ? (
        // Weekly View - Single Row
        <div className="grid grid-cols-7">
          {weekDays.map((date) => {
            const dateStr = formatDateString(date);
            const selected = isSelected(date);
            const todayDate = isToday(date);
            
            return (
              <button
                key={dateStr}
                onClick={() => onDateSelect && onDateSelect(dateStr)}
                className={`relative py-3 flex flex-col items-center transition-all hover:bg-secondary/50 ${
                  selected ? 'bg-accent/10' : ''
                } ${todayDate && !selected ? 'ring-1 ring-accent ring-inset' : ''}`}
                data-testid={`day-${dateStr}`}
              >
                <span className={`text-lg font-semibold ${
                  selected ? 'text-accent' : todayDate ? 'text-accent' : ''
                }`}>
                  {date.getDate()}
                </span>
                {renderDayContent && renderDayContent(dateStr)}
              </button>
            );
          })}
        </div>
      ) : (
        // Monthly View - Grid
        <div className="grid grid-cols-7">
          {monthGrid.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="py-2" />;
            }
            
            const dateStr = formatDateString(date);
            const selected = isSelected(date);
            const todayDate = isToday(date);
            
            return (
              <button
                key={dateStr}
                onClick={() => onDateSelect && onDateSelect(dateStr)}
                className={`relative py-2 flex flex-col items-center transition-all hover:bg-secondary/50 ${
                  selected && !todayDate ? 'bg-accent/10' : ''
                } ${todayDate ? 'ring-1 ring-accent ring-inset' : ''}`}
                data-testid={`day-${dateStr}`}
              >
                <span className={`text-sm font-medium ${
                  todayDate ? 'text-accent' : selected ? 'text-accent' : ''
                }`}>
                  {date.getDate()}
                </span>
                {renderDayContent && renderDayContent(dateStr)}
              </button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

export default CalendarView;
